import { resolveDataEndpoint } from '@/utils/resolveDataEndpoint';
import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Alert,
  Chip,
  Button,
  IconButton,
  Tabs,
  Tab,
  Popover,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  useGridApiRef,
  type GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import {
  KeyboardArrowRight as ArrowRightIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ViewList as ListIcon,
  History as HistoryIcon,
  Check as CheckIcon,
  ViewColumn as ViewColumnIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  UnfoldMore as ExpandAllIcon,
  UnfoldLess as CollapseAllIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useEntities, useHierarchyEntities } from '@/hooks';
import { LoadingSkeleton } from '@/components/shared/LoadingSkeleton';
import type {
  Entity,
  EntityListColumnConfig,
  EntityListConfiguration,
  EntityListDataSourceConfig,
} from '@/types/Entity';
import type { EntityTypeConfiguration } from '@/services/ConfigService';
import { API_CONFIG } from '@/config/api';
import type { HierarchyEntity } from '@/types/hierarchy';
import type { EntityActionConfig } from '@/types/Entity';
import { getEntityAction } from '@/actions/entityActionRegistry';
import { useQueryClient } from '@tanstack/react-query';
import { useNotifications } from '@/stores/useNotificationStore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEntityActionDialogs } from '@/components/entities/EntityActionDialogProvider';
import { EntityFilterBar } from '@/components/shared/EntityFilterBar';
import { EntityActivityPanel } from '@/components/shared/EntityActivityPanel';
import { logger } from '@equip/utils/logger';
import { useAuth } from '@/hooks/useAuth';
import { useMyPersonEntityId } from '@/hooks/useMyPersonEntityId';
import { useIeKitLabels, useIeKitIdPrefix } from '@/hooks/useIeKitLabels';
import { isReadOnlyEntityAction } from '@/utils/entityTypeConfig';
import { formatNomenclatureWithSize } from '@equip/utils';
import { usePersonLabels } from '@/hooks/usePersonLabels';
import { usePeopleSyncWorkerEnabled } from '@/hooks/queries/admin/useSystemHealth';
import {
  useColumnPreferencesStore,
  type ColumnVisibilityModel,
} from '@/stores/useColumnPreferencesStore';
const log = logger.child({ component: 'EntityList' });

/**
 * Sort order for location types in the hierarchy tree.
 * Site → Row → Shelf → Kiosk. Kiosk is always a leaf node.
 */
const LOCATION_TYPE_SORT_ORDER: Record<string, number> = {
  Site: 0,
  Row: 1,
  Shelf: 2,
  Kiosk: 3,
};

/** Sort hierarchy children so they appear grouped by type in the canonical order. */
const sortHierarchyChildren = (children: HierarchyEntity[]): HierarchyEntity[] => {
  return [...children].sort((a, b) => {
    const aTypeName = (a as Record<string, unknown>).node_type_name as string | undefined;
    const bTypeName = (b as Record<string, unknown>).node_type_name as string | undefined;
    const aOrder = LOCATION_TYPE_SORT_ORDER[aTypeName ?? ''] ?? 99;
    const bOrder = LOCATION_TYPE_SORT_ORDER[bTypeName ?? ''] ?? 99;
    if (aOrder !== bOrder) return aOrder - bOrder;
    // Within the same type, sort alphabetically by display_name
    const aName = (a.display_name ?? '').toLowerCase();
    const bName = (b.display_name ?? '').toLowerCase();
    return aName < bName ? -1 : aName > bName ? 1 : 0;
  });
};

/** Maps entity-action handler ids to compact icon elements for the Actions column. */
const actionIconMap: Record<string, React.ReactNode> = {
  'catalog.view': <ViewIcon fontSize="small" />,
  'catalog.edit': <EditIcon fontSize="small" />,
  'location.view': <ViewIcon fontSize="small" />,
  'location.edit': <EditIcon fontSize="small" />,
  'location.delete': <DeleteIcon fontSize="small" />,
  'person.view': <ViewIcon fontSize="small" />,
  'ieKit.manage': <EditIcon fontSize="small" />,
};

type UnknownRecord = Record<string, unknown>;

type EntityGridRow = Entity & {
  __depth?: number;
  __parentId?: string | null;
  __hasChildren?: boolean;
};

const DEFAULT_LIST_CONFIGURATION: EntityListConfiguration = {
  columns: [
    { field: 'entity_identifier', label: 'ID', width: 150, sortable: true },
    { field: 'display_name', label: 'Name', flex: 1, sortable: true },
    { field: 'entity_type', label: 'Type', width: 120, sortable: true },
    {
      field: 'status',
      label: 'Status',
      width: 100,
      sortable: true,
      type: 'status',
    },
    { field: 'updated_at', label: 'Last Updated', width: 180, sortable: true, type: 'dateTime' },
  ],
  export: {
    fields: ['id', 'entity_identifier', 'display_name', 'entity_type', 'status', 'updated_at'],
  },
};

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

type FieldTarget = 'root' | 'entity_data' | 'metadata';

const parseFieldPath = (path: string): { target: FieldTarget; key: string } => {
  if (path.startsWith('entity_data.')) {
    return { target: 'entity_data', key: path.slice('entity_data.'.length) };
  }
  if (path.startsWith('metadata.')) {
    return { target: 'metadata', key: path.slice('metadata.'.length) };
  }
  return { target: 'root', key: path };
};

const getNestedValue = (source: UnknownRecord | undefined, keyPath: string): unknown => {
  if (!source) return undefined;
  const parts = keyPath.split('.');
  let cursor: unknown = source;
  for (const part of parts) {
    if (!isRecord(cursor)) return undefined;
    cursor = cursor[part];
    if (cursor === undefined) return undefined;
  }
  return cursor;
};

const getValueAtPath = (source: Partial<Entity> | Entity | undefined, path: string): unknown => {
  if (!source) return undefined;
  const { target, key } = parseFieldPath(path);
  if (target === 'root') {
    return (source as UnknownRecord)[key];
  }
  if (target === 'entity_data') {
    return getNestedValue(source.entity_data as UnknownRecord | undefined, key);
  }
  return getNestedValue(source.metadata as UnknownRecord | undefined, key);
};

const formatHeaderLabel = (field: string): string => {
  const segment = field.split('.').pop() ?? field;
  return segment
    .split('_')
    .map((part) => (part ? part[0]!.toUpperCase() + part.slice(1) : ''))
    .join(' ');
};

const formatTemporalValue = (value: unknown, type: 'date' | 'dateTime'): string => {
  if (!value) return '';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  if (type === 'date') return date.toLocaleDateString();
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
};

const buildExportFileName = (template: string | undefined, entityType: string): string => {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  if (!template) {
    return `${entityType}-entities-${timestamp}.csv`;
  }
  const interpolated = template
    .replace(/{{\s*entityType\s*}}/gi, entityType)
    .replace(/{{\s*timestamp\s*}}/gi, timestamp);
  return interpolated.toLowerCase().endsWith('.csv') ? interpolated : `${interpolated}.csv`;
};

interface EntityListProps {
  entityType: string;
  title?: string;
  onEntityClick?: (entity: Entity) => void;
  entityConfig?: EntityTypeConfiguration;
  filters?: Record<string, string>;
  onClearFilters?: () => void;
}

export const EntityList: React.FC<EntityListProps> = ({
  entityType,
  title,
  onEntityClick: _onEntityClick,
  entityConfig,
  filters,
  onClearFilters,
}) => {
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(() => new Set());
  const expandedNodeIdsRef = useRef(expandedNodeIds);
  expandedNodeIdsRef.current = expandedNodeIds;
  const [activeTab, setActiveTab] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const [hierarchySortField, setHierarchySortField] = useState<string | null>(null);
  const [hierarchySortDir, setHierarchySortDir] = useState<'asc' | 'desc'>('asc');
  const [columnSelectorAnchor, setColumnSelectorAnchor] = useState<HTMLElement | null>(null);
  const { openDialog, closeDialog } = useEntityActionDialogs();
  const queryClient = useQueryClient();
  const notifications = useNotifications();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const gridApiRef = useGridApiRef();
  const { user, hasRole, isViewer } = useAuth();
  const { myPersonEntityId } = useMyPersonEntityId();
  const isServiceMember = hasRole('service-member');
  const effectiveUserId = user?.id ?? 'anonymous';
  const columnPrefKey = `entity-list-${entityType}`;
  const personLabels = usePersonLabels('front_window');
  const { data: peopleSyncWorkerEnabled = true } = usePeopleSyncWorkerEnabled({
    enabled: entityType === 'person',
  });
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const { formatIdentifier: formatIeKitId } = useIeKitIdPrefix();

  const rawListConfig = entityConfig?.ui_configuration?.list;
  const listDataSource: EntityListDataSourceConfig | undefined = rawListConfig?.dataSource as
    | EntityListDataSourceConfig
    | undefined;
  const isHierarchySource = listDataSource?.type === 'hierarchy';
  const resolvedHierarchyDataSource = listDataSource?.endpoint
    ? { endpoint: resolveDataEndpoint(listDataSource.endpoint), order: listDataSource.order }
    : { endpoint: API_CONFIG.ENDPOINTS.ENTITIES, order: listDataSource?.order };

  const {
    data: defaultEntities = [],
    isLoading: entitiesLoading,
    error: entitiesError,
    refetch: entitiesRefetch,
  } = useEntities({
    type: entityType,
    filters: filters,
    enabled: !isHierarchySource,
  });

  const {
    data: hierarchyEntities = [],
    isLoading: hierarchyLoading,
    error: hierarchyError,
    refetch: hierarchyRefetch,
  } = useHierarchyEntities({
    dataSource: resolvedHierarchyDataSource,
    filters: filters,
    enabled: isHierarchySource,
  });

  // When a node_type_name filter is active (e.g. filtering locations by type), the filtered
  // response contains only matching nodes. Child nodes of different types are absent, so
  // childrenMap has no entries for them and expand icons disappear entirely.
  // A supplementary query fetches the full hierarchy (without the type filter) so the tree
  // can be built correctly: filtered nodes appear at root, all children visible on expansion.
  const hasTypeFilter = isHierarchySource && Boolean(filters?.['node_type_name']);
  const filtersForTreeExpansion = useMemo<Record<string, string> | undefined>(() => {
    if (!isHierarchySource || !filters?.['node_type_name'] || !filters) return undefined;
    const rest = Object.fromEntries(
      Object.entries(filters).filter(([key]) => key !== 'node_type_name'),
    );
    return Object.keys(rest).length > 0 ? rest : undefined;
  }, [isHierarchySource, filters]);

  const { data: treeExpansionEntities = [] } = useHierarchyEntities({
    dataSource: resolvedHierarchyDataSource,
    filters: filtersForTreeExpansion,
    enabled: hasTypeFilter,
  });

  const entities = (isHierarchySource ? hierarchyEntities : defaultEntities) as Entity[];
  const isLoading = isHierarchySource ? hierarchyLoading : entitiesLoading;
  const error = (isHierarchySource ? hierarchyError : entitiesError) as Error | null | undefined;
  const refetch = isHierarchySource ? hierarchyRefetch : entitiesRefetch;

  // For catalog entities, build a lookup of which items inherit expiration from their parent.
  // Both parents and children are in the same entities array, so we can resolve locally.
  const catalogExpInheritMap = useMemo(() => {
    if (entityType !== 'catalog') return new Map<string, string>();
    const map = new Map<string, string>();
    const entityById = new Map(entities.map((e) => [e.id, e]));
    for (const entity of entities) {
      const parentId = (entity.entity_data as Record<string, unknown>)?.parent_id as
        | string
        | undefined;
      if (!parentId) continue;
      const parent = entityById.get(parentId);
      if (!parent) continue;
      const childHasExp =
        Boolean((entity.entity_data as Record<string, unknown>)?.expiration_start_date) ||
        Boolean(
          (entity.entity_data as Record<string, unknown>)?.expiration_duration_from_start_date,
        );
      const parentExpStart = (parent.entity_data as Record<string, unknown>)?.expiration_start_date;
      const parentExpDur = (parent.entity_data as Record<string, unknown>)
        ?.expiration_duration_from_start_date;
      const parentHasExp = Boolean(parentExpStart) || Boolean(parentExpDur);
      if (!childHasExp && parentHasExp) {
        const label = `${String(parentExpStart ?? '')} / ${String(parentExpDur ?? '')}d`;
        map.set(entity.id, label);
      }
    }
    return map;
  }, [entities, entityType]);
  const catalogExpInheritMapRef = useRef(catalogExpInheritMap);
  catalogExpInheritMapRef.current = catalogExpInheritMap;

  useEffect(() => {
    if (!isHierarchySource) return;

    if (hierarchyEntities.length === 0) {
      setExpandedNodeIds((prev) => (prev.size === 0 ? prev : new Set<string>()));
      return;
    }

    setExpandedNodeIds((prev) => {
      const ids = new Set(hierarchyEntities.map((entity) => entity.id));
      let changed = false;
      const next = new Set<string>();

      prev.forEach((id) => {
        if (ids.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [hierarchyEntities, isHierarchySource]);

  const toggleNodeExpansion = useCallback((nodeId: string) => {
    if (!nodeId) return;

    setExpandedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  /** Expand all parent nodes in the hierarchy. */
  const expandAllNodes = useCallback(() => {
    if (!isHierarchySource) return;
    const sourceEntities =
      hasTypeFilter && treeExpansionEntities.length > 0 ? treeExpansionEntities : hierarchyEntities;
    const parentIds = new Set<string>();
    const idSet = new Set(sourceEntities.map((e) => e.id));
    for (const entity of sourceEntities) {
      if (entity.parent_id && idSet.has(entity.parent_id)) {
        parentIds.add(entity.parent_id);
      }
    }
    setExpandedNodeIds(parentIds);
  }, [isHierarchySource, hierarchyEntities, hasTypeFilter, treeExpansionEntities]);

  /** Collapse all nodes. */
  const collapseAllNodes = useCallback(() => {
    setExpandedNodeIds(new Set());
  }, []);

  const filterEntries = useMemo(() => {
    if (!filters) return [] as Array<[string, string]>;
    return Object.entries(filters).filter(
      ([, value]) => value !== '' && value !== undefined && value !== null,
    );
  }, [filters]);

  const columnConfigs = useMemo<EntityListColumnConfig[]>(() => {
    const base =
      rawListConfig?.columns && rawListConfig.columns.length > 0
        ? rawListConfig.columns
        : (DEFAULT_LIST_CONFIGURATION.columns ?? []);
    // For person entities, override the individual_id column label with the configured value
    if (entityType === 'person') {
      return base.map((col) =>
        col.field === 'entity_data.individual_id'
          ? { ...col, label: personLabels.individualIdLabel }
          : col,
      );
    }
    return base;
  }, [rawListConfig, entityType, personLabels.individualIdLabel]);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    () => {
      const stored = useColumnPreferencesStore
        .getState()
        .getVisibility(effectiveUserId, columnPrefKey);
      const model: GridColumnVisibilityModel = {};
      columnConfigs.forEach((col) => {
        model[col.field] = stored?.[col.field] !== false;
      });
      return model;
    },
  );

  useEffect(() => {
    const stored = useColumnPreferencesStore
      .getState()
      .getVisibility(effectiveUserId, columnPrefKey);
    const model: GridColumnVisibilityModel = {};
    columnConfigs.forEach((col) => {
      model[col.field] = stored?.[col.field] !== false;
    });
    setColumnVisibilityModel(model);
  }, [effectiveUserId, columnPrefKey, columnConfigs]);

  const exportFields = useMemo(() => {
    if (rawListConfig?.export?.fields && rawListConfig.export.fields.length > 0) {
      return rawListConfig.export.fields;
    }
    if (rawListConfig?.columns) {
      const combined = new Set<string>();
      rawListConfig.columns.forEach((col) => combined.add(col.field));
      (DEFAULT_LIST_CONFIGURATION.export?.fields ?? []).forEach((field) => combined.add(field));
      return Array.from(combined);
    }
    return DEFAULT_LIST_CONFIGURATION.export?.fields ?? [];
  }, [rawListConfig]);

  const exportFileNameTemplate = rawListConfig?.export?.fileNameTemplate;

  const columnLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    columnConfigs.forEach((col) => {
      map.set(col.field, col.label ?? formatHeaderLabel(col.field));
    });
    return map;
  }, [columnConfigs]);

  const handleToggleColumn = useCallback(
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = useColumnPreferencesStore
        .getState()
        .updateColumn(effectiveUserId, columnPrefKey, field, event.target.checked);
      const model: GridColumnVisibilityModel = {};
      columnConfigs.forEach((col) => {
        model[col.field] = next[col.field] !== false;
      });
      setColumnVisibilityModel(model);
    },
    [effectiveUserId, columnPrefKey, columnConfigs],
  );

  const handleVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      const sanitized: GridColumnVisibilityModel = {};
      columnConfigs.forEach((col) => {
        sanitized[col.field] = model[col.field] !== false;
      });
      setColumnVisibilityModel((prev) => {
        const prevKeys = Object.keys(prev).sort();
        const nextKeys = Object.keys(sanitized).sort();
        if (
          prevKeys.length === nextKeys.length &&
          prevKeys.every((key, i) => key === nextKeys[i] && prev[key] === sanitized[key])
        ) {
          return prev;
        }
        const sanitizedForStore = columnConfigs.reduce<ColumnVisibilityModel>((acc, col) => {
          acc[col.field] = sanitized[col.field] ?? true;
          return acc;
        }, {} as ColumnVisibilityModel);
        useColumnPreferencesStore
          .getState()
          .setVisibility(effectiveUserId, columnPrefKey, sanitizedForStore);
        return sanitized;
      });
    },
    [effectiveUserId, columnPrefKey, columnConfigs],
  );

  const listItemActions = useMemo(() => {
    if (!entityConfig?.ui_configuration?.actions) {
      return [] as EntityActionConfig[];
    }
    return entityConfig.ui_configuration.actions.filter((action) => {
      if (action.scope !== 'list_item') return false;
      if (action.requiredRoles && !action.requiredRoles.some((r) => hasRole(r))) return false;
      if (isViewer && !isReadOnlyEntityAction(action)) return false;
      return Boolean(getEntityAction(action.handler));
    });
  }, [entityConfig, isViewer, hasRole]);

  const listToolbarActions = useMemo(() => {
    if (!entityConfig?.ui_configuration?.actions) {
      return [] as EntityActionConfig[];
    }
    return entityConfig.ui_configuration.actions
      .filter((action) => {
        if (action.scope !== 'list_toolbar') return false;
        if (action.requiredRoles && !action.requiredRoles.some((r) => hasRole(r))) return false;
        if (isViewer && !isReadOnlyEntityAction(action)) return false;
        // Service members on the person list cannot run admin write actions (sync, bulk import)
        if (isServiceMember && entityType === 'person' && !isReadOnlyEntityAction(action))
          return false;
        // Hide the People Sync action when the worker is disabled in System Health.
        if (action.id === 'person.sync' && !peopleSyncWorkerEnabled) return false;
        return Boolean(getEntityAction(action.handler));
      })
      .map((action) => {
        // Override ie_kit action labels with the configurable IE Kit label
        if (
          entityType === 'ie_kit' &&
          action.scope === 'list_toolbar' &&
          action.id === 'ie-kit.create'
        ) {
          return { ...action, label: `Create ${ieKitLabel}` };
        }
        return action;
      });
  }, [
    entityConfig,
    isViewer,
    hasRole,
    isServiceMember,
    entityType,
    ieKitLabel,
    peopleSyncWorkerEnabled,
  ]);

  const runEntityAction = useCallback(
    async (action: EntityActionConfig, entity?: EntityGridRow) => {
      if (!entityConfig) return;
      const handler = getEntityAction(action.handler);
      if (!handler) return;

      try {
        await handler(
          {
            entity,
            entityTypeConfig: entityConfig,
            queryClient,
            notifications,
            navigate,
            returnSearch: searchParams.toString() || undefined,
            openDialog,
            closeDialog,
          },
          action,
        );
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unexpected error';
        notifications.error('Action failed', message);
        log.error('Action handler failed', { handler: action.handler, error: String(err) });
      }
    },
    [entityConfig, queryClient, notifications, navigate, searchParams, openDialog, closeDialog],
  );

  const rows = useMemo<EntityGridRow[]>(() => {
    if (!entities?.length) return [];

    if (!isHierarchySource) {
      return entities as EntityGridRow[];
    }

    // When a node_type_name filter is active, use the full tree data so that expand icons
    // appear correctly and children of different types are shown on expansion.
    if (hasTypeFilter && treeExpansionEntities.length > 0) {
      const fullChildrenMap = new Map<string, HierarchyEntity[]>();
      for (const entity of treeExpansionEntities) {
        if (!entity.parent_id) continue;
        const siblings = fullChildrenMap.get(entity.parent_id);
        if (siblings) {
          siblings.push(entity);
        } else {
          fullChildrenMap.set(entity.parent_id, [entity]);
        }
      }

      const buildExpanded = (parentId: string, depth: number): EntityGridRow[] => {
        const children = fullChildrenMap.get(parentId);
        if (!children) return [];
        const sorted = sortHierarchyChildren(children);
        const flattened: EntityGridRow[] = [];
        sorted.forEach((child) => {
          const childHasChildren = fullChildrenMap.has(child.id);
          const nodeRow: EntityGridRow = {
            ...child,
            __depth: depth,
            __parentId: parentId,
            __hasChildren: childHasChildren,
          };
          flattened.push(nodeRow);
          if (childHasChildren && expandedNodeIds.has(child.id)) {
            flattened.push(...buildExpanded(child.id, depth + 1));
          }
        });
        return flattened;
      };

      const result: EntityGridRow[] = [];
      for (const entity of hierarchyEntities) {
        const hasChildren = fullChildrenMap.has(entity.id);
        result.push({
          ...entity,
          __depth: 0,
          __parentId: null,
          __hasChildren: hasChildren,
        });
        if (hasChildren && expandedNodeIds.has(entity.id)) {
          result.push(...buildExpanded(entity.id, 1));
        }
      }
      return result;
    }

    const ids = new Set(hierarchyEntities.map((entity) => entity.id));
    const childrenMap = new Map<string | null, HierarchyEntity[]>();

    hierarchyEntities.forEach((entity) => {
      const parentKey = entity.parent_id && ids.has(entity.parent_id) ? entity.parent_id : null;
      const siblings = childrenMap.get(parentKey);
      if (siblings) {
        siblings.push(entity);
      } else {
        childrenMap.set(parentKey, [entity]);
      }
    });

    const rootFilter = listDataSource?.rootFilter;
    // When a text search is active (or param present), disable the rootFilter so that
    // matching child locations (shelves, rows, etc.) appear as top-level results.
    const hasTextSearch = Boolean(filters?.['or']);
    const applyRootFilter = rootFilter && !hasTextSearch;

    const buildRows = (parentId: string | null, depth: number): EntityGridRow[] => {
      const children = childrenMap.get(parentId);
      if (!children) return [];

      const sorted = sortHierarchyChildren(children);
      const flattened: EntityGridRow[] = [];
      sorted.forEach((child) => {
        // At root level, skip entities that don't match the rootFilter (if configured).
        // This ensures e.g. orphan Rows/Shelves/Kiosks don't appear as top-level items.
        // Entities where the field is null/undefined are still shown (untyped roots).
        if (depth === 0 && applyRootFilter) {
          const fieldValue = (child as Record<string, unknown>)[rootFilter.field];
          if (fieldValue != null && fieldValue !== rootFilter.value) return;
        }

        const hasChildren = childrenMap.has(child.id);
        const nodeRow: EntityGridRow = {
          ...child,
          __depth: depth,
          __parentId: parentId,
          __hasChildren: hasChildren,
        };

        flattened.push(nodeRow);

        if (hasChildren && expandedNodeIds.has(child.id)) {
          flattened.push(...buildRows(child.id, depth + 1));
        }
      });

      return flattened;
    };

    return buildRows(null, 0);
  }, [
    entities,
    hierarchyEntities,
    isHierarchySource,
    expandedNodeIds,
    hasTypeFilter,
    treeExpansionEntities,
    listDataSource?.rootFilter,
    filters,
  ]);

  const handleHierarchySort = useCallback(
    (field: string) => {
      setHierarchySortField((prev) => {
        if (prev === field) {
          if (hierarchySortDir === 'asc') {
            setHierarchySortDir('desc');
            return field;
          }
          // Already desc → clear sort
          setHierarchySortDir('asc');
          return null;
        }
        setHierarchySortDir('asc');
        return field;
      });
    },
    [hierarchySortDir],
  );

  /** Sort top-level rows while keeping children grouped under their parent. */
  const sortedHierarchyRows = useMemo(() => {
    if (!hierarchySortField) return rows;

    // Separate rows into roots and a children-by-parent lookup.
    const roots: EntityGridRow[] = [];
    const childrenByParent = new Map<string, EntityGridRow[]>();
    for (const row of rows) {
      if ((row.__depth ?? 0) === 0) {
        roots.push(row);
      } else if (row.__parentId) {
        const siblings = childrenByParent.get(row.__parentId) ?? [];
        siblings.push(row);
        childrenByParent.set(row.__parentId, siblings);
      }
    }

    const compare = (a: EntityGridRow, b: EntityGridRow) => {
      const aRaw = getValueAtPath(a, hierarchySortField);
      const bRaw = getValueAtPath(b, hierarchySortField);
      const aVal =
        aRaw === null || aRaw === undefined
          ? ''
          : typeof aRaw === 'string'
            ? aRaw.toLowerCase()
            : aRaw;
      const bVal =
        bRaw === null || bRaw === undefined
          ? ''
          : typeof bRaw === 'string'
            ? bRaw.toLowerCase()
            : bRaw;
      if (aVal === bVal) return 0;
      const result = aVal < bVal ? -1 : 1;
      return hierarchySortDir === 'asc' ? result : -result;
    };

    roots.sort(compare);

    // Rebuild the flat list: root followed by its sorted children.
    const sorted: EntityGridRow[] = [];
    for (const root of roots) {
      sorted.push(root);
      const children = childrenByParent.get(root.id);
      if (children) {
        children.sort(compare);
        sorted.push(...children);
      }
    }
    return sorted;
  }, [rows, hierarchySortField, hierarchySortDir]);

  const hierarchyRootCount = useMemo(() => {
    if (!isHierarchySource) return 0;
    return sortedHierarchyRows.filter((r) => (r.__depth ?? 0) === 0).length;
  }, [sortedHierarchyRows, isHierarchySource]);

  const totalTopLevelCount = useMemo(() => {
    if (!isHierarchySource || !hierarchyEntities.length) return undefined;
    const ids = new Set(hierarchyEntities.map((e) => e.id));
    return hierarchyEntities.filter((e) => !e.parent_id || !ids.has(e.parent_id)).length;
  }, [hierarchyEntities, isHierarchySource]);

  const toCsvValue = (v: unknown) => {
    if (v === null || v === undefined) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    const needsWrap = /[",\n]/.test(s);
    const escaped = s.replace(/"/g, '""');
    return needsWrap ? `"${escaped}"` : escaped;
  };

  const handleExportCsv = useCallback(() => {
    const fields = exportFields.length > 0 ? exportFields : ['id'];
    const headers = fields.map((field) => columnLabelMap.get(field) ?? formatHeaderLabel(field));
    const lines = [headers.join(',')];
    entities.forEach((row) => {
      const rowValues = fields.map((field) => toCsvValue(getValueAtPath(row, field)));
      lines.push(rowValues.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = buildExportFileName(exportFileNameTemplate, entityType);
    a.click();
    URL.revokeObjectURL(url);
  }, [entities, exportFields, columnLabelMap, exportFileNameTemplate, entityType]);

  const hasListItemActions = listItemActions.length > 0;
  const actionColumnWidth = useMemo(() => {
    if (!hasListItemActions) return 0;
    const totalWidth = listItemActions.reduce((w, a) => {
      return w + (actionIconMap[a.handler] ? 40 : 110);
    }, 16);
    return Math.max(totalWidth, 56);
  }, [hasListItemActions, listItemActions]);

  const columns: GridColDef<EntityGridRow>[] = useMemo(() => {
    const gridColumns: GridColDef<EntityGridRow>[] = columnConfigs.map((col) => {
      const field = col.field;
      const baseColumn: GridColDef<EntityGridRow> = {
        field,
        headerName: col.label ?? formatHeaderLabel(field),
        width: col.width,
        minWidth: col.minWidth,
        flex: col.flex,
        sortable: col.sortable ?? true,
        align: col.align,
        headerAlign: col.align,
        valueGetter: (_value, row) => {
          const raw = getValueAtPath(row, field);
          if (field === 'display_name') {
            const size = getValueAtPath(row, 'entity_data.size');
            const baseName = getValueAtPath(row, 'entity_data.name');
            if (size && baseName) return formatNomenclatureWithSize(String(baseName), String(size));
          }
          // Show configured prefix for ie_kit entity identifiers
          if (field === 'entity_identifier' && entityType === 'ie_kit' && raw) {
            return formatIeKitId(String(raw));
          }
          // Show configured prefix for person entity identifiers
          if (field === 'entity_identifier' && entityType === 'person' && raw) {
            return String(raw).replace(/^INDV-/, personLabels.individualIdPrefix);
          }
          // Show configured label instead of raw entity_type
          if (field === 'entity_type' && raw === 'ie_kit') {
            return ieKitLabel;
          }
          if (field === 'entity_type' && entityConfig?.display_name && raw === entityType) {
            return entityConfig.display_name;
          }
          return raw;
        },
      };

      if (col.type === 'date' || col.type === 'dateTime') {
        baseColumn.renderCell = (params) => {
          if (!params?.row) return '';
          return formatTemporalValue(
            params.value ?? getValueAtPath(params.row, field),
            col.type as 'date' | 'dateTime',
          );
        };
      }

      if (col.type === 'status') {
        baseColumn.renderCell = (params) => {
          if (!params?.row) return <Chip label="-" size="small" variant="outlined" />;
          const raw = params.value ?? getValueAtPath(params.row, field);
          if (!raw) {
            return <Chip label="-" size="small" variant="outlined" />;
          }
          const status = String(raw);
          const color: 'default' | 'success' | 'warning' | 'error' =
            status === 'active' ? 'success' : status === 'inactive' ? 'warning' : 'error';
          return <Chip label={status} size="small" color={color} />;
        };
      }

      if (col.type === 'boolean') {
        baseColumn.renderCell = (params) => {
          if (!params?.row) return null;
          const raw = params.value ?? getValueAtPath(params.row, field);
          if (raw === true || raw === 'true') {
            return <CheckIcon fontSize="small" color="success" />;
          }
          return null;
        };
      }

      if (isHierarchySource && field === columnConfigs[0]?.field) {
        if (!baseColumn.minWidth) {
          baseColumn.minWidth = 200;
        }
        baseColumn.renderCell = (params) => {
          if (!params?.row) return '';
          const row = params.row;
          const depth = row.__depth ?? 0;
          const hasChildren = Boolean(row.__hasChildren);
          const isExpanded = Boolean(row.id && expandedNodeIdsRef.current.has(row.id));
          const label = params.value ?? getValueAtPath(row, field) ?? '';
          const displayLabel = String(label);

          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                pl: depth * 2,
                gap: 0.5,
                height: '100%',
              }}
            >
              {hasChildren ? (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleNodeExpansion(row.id);
                  }}
                  aria-label={isExpanded ? 'Collapse node' : 'Expand node'}
                  sx={{ p: 0.5 }}
                >
                  {isExpanded ? (
                    <ArrowDownIcon fontSize="small" />
                  ) : (
                    <ArrowRightIcon fontSize="small" />
                  )}
                </IconButton>
              ) : (
                <Box component="span" sx={{ width: 32, display: 'inline-flex' }} />
              )}
              <Typography
                variant="body2"
                component="span"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {displayLabel}
              </Typography>
            </Box>
          );
        };
      }

      if (!baseColumn.renderCell) {
        // For the person entity display_name column: show a "You" chip on the
        // session user’s own record so service members and other users can
        // instantly identify their row.
        if (field === 'display_name' && entityType === 'person') {
          baseColumn.renderCell = (params) => {
            if (!params?.row) return '';
            const raw = params.value ?? getValueAtPath(params.row, field);
            const isOwnRow = myPersonEntityId && params.row.id === myPersonEntityId;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" component="span">
                  {raw !== undefined && raw !== null ? String(raw) : ''}
                </Typography>
                {isOwnRow && (
                  <Chip
                    label="You"
                    size="small"
                    color="primary"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            );
          };
        } else {
          baseColumn.renderCell = (params) => {
            if (!params?.row) return '';
            const raw = params.value ?? getValueAtPath(params.row, field);
            if (raw === undefined || raw === null) {
              return '';
            }
            return <>{String(raw)}</>;
          };
        }
      }

      return baseColumn;
    });

    // For catalog entities, insert an Expiration column showing own or inherited status
    if (entityType === 'catalog') {
      gridColumns.push({
        field: '__expiration_status',
        headerName: 'Expiration',
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          if (!params?.row) return null;
          const row = params.row;
          const expStart = getValueAtPath(row, 'entity_data.expiration_start_date');
          const expDur = getValueAtPath(row, 'entity_data.expiration_duration_from_start_date');
          if (expStart || expDur) {
            return (
              <Tooltip title={`${String(expStart ?? '')} / ${String(expDur ?? '')} days`}>
                <Chip
                  label={`${String(expStart ?? '')} / ${String(expDur ?? '')}d`}
                  size="small"
                  color="default"
                  variant="outlined"
                />
              </Tooltip>
            );
          }
          const inheritedLabel = catalogExpInheritMapRef.current.get(row.id);
          if (inheritedLabel) {
            return (
              <Tooltip title={`Inherited from parent: ${inheritedLabel}`}>
                <Chip
                  icon={<ScheduleIcon />}
                  label="Inherited"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Tooltip>
            );
          }
          return null;
        },
      });
    }

    gridColumns.push({
      field: 'actions',
      headerName: 'Actions',
      width: actionColumnWidth,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (!params?.row) return null;
        const row = params.row;
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', height: '100%' }}>
            {listItemActions.map((action) => {
              if (action.visibleWhenStatus && row.status !== action.visibleWhenStatus) {
                return null;
              }
              if (action.requiresOwnRecord && row.id !== myPersonEntityId) {
                return null;
              }
              const icon = actionIconMap[action.handler] ?? null;
              return icon ? (
                <Tooltip key={action.id} title={action.label}>
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label={action.label}
                      onClick={(event) => {
                        event.stopPropagation();
                        void runEntityAction(action, row);
                      }}
                    >
                      {icon}
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Button
                  key={action.id}
                  size="small"
                  variant="outlined"
                  onClick={(event) => {
                    event.stopPropagation();
                    void runEntityAction(action, row);
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  {action.label}
                </Button>
              );
            })}
          </Box>
        );
      },
    });

    return gridColumns;
  }, [
    columnConfigs,
    entityType,
    isHierarchySource,
    toggleNodeExpansion,
    listItemActions,
    runEntityAction,
    actionColumnWidth,
    myPersonEntityId,
  ]);

  if (isLoading) {
    return <LoadingSkeleton variant="grid" count={10} />;
  }

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        Error loading entities: {error.message}
      </Alert>
    );
  }

  const selectorOpen = Boolean(columnSelectorAnchor);
  const selectorId = selectorOpen ? 'entity-list-column-selector' : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Service-member info banner on the Person list */}
      {isServiceMember && entityType === 'person' && (
        <Alert severity="info" sx={{ mb: 1 }}>
          You can view all records, but Sizing is only available for your own record.
        </Alert>
      )}
      <EntityFilterBar
        title={title}
        entityType={entityType}
        entityCount={entities.length}
        topLevelCount={totalTopLevelCount}
        actions={listToolbarActions}
        onActionClick={(action) => void runEntityAction(action)}
        onExport={!isViewer && activeTab === 0 ? handleExportCsv : undefined}
        activeFilters={filterEntries}
        onClearFilters={onClearFilters}
      />

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2, minHeight: 36 }}
        >
          <Tab
            icon={<ListIcon />}
            iconPosition="start"
            label="List"
            sx={{ minHeight: 36, py: 0 }}
          />
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label="Activity"
            sx={{ minHeight: 36, py: 0 }}
          />
        </Tabs>

        <CardContent
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            py: 1,
            '&:last-child': { pb: 1 },
          }}
        >
          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              {isHierarchySource && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Showing {sortedHierarchyRows.length} of {rows.length} items (
                    {hierarchyRootCount} top-level)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Expand all nodes">
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<ExpandAllIcon fontSize="small" />}
                        onClick={expandAllNodes}
                        sx={{ textTransform: 'none' }}
                      >
                        Expand All
                      </Button>
                    </Tooltip>
                    <Tooltip title="Collapse all nodes">
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<CollapseAllIcon fontSize="small" />}
                        onClick={collapseAllNodes}
                        disabled={expandedNodeIds.size === 0}
                        sx={{ textTransform: 'none' }}
                      >
                        Collapse All
                      </Button>
                    </Tooltip>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ViewColumnIcon fontSize="small" />}
                      onClick={(event) => setColumnSelectorAnchor(event.currentTarget)}
                      aria-describedby={selectorId}
                    >
                      Manage Columns
                    </Button>
                  </Box>
                </Box>
              )}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {isHierarchySource ? (
                  <Box sx={{ overflow: 'auto', flex: '1 1 0', height: 0 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {columnConfigs
                            .filter((col) => columnVisibilityModel[col.field] !== false)
                            .map((col) => (
                              <TableCell
                                key={col.field}
                                align={col.align}
                                sortDirection={
                                  hierarchySortField === col.field ? hierarchySortDir : false
                                }
                                sx={{
                                  fontWeight: 600,
                                  ...(col.width ? { width: col.width, minWidth: col.width } : {}),
                                  ...(col.flex ? { width: '100%' } : {}),
                                }}
                              >
                                {col.sortable !== false ? (
                                  <TableSortLabel
                                    active={hierarchySortField === col.field}
                                    direction={
                                      hierarchySortField === col.field ? hierarchySortDir : 'asc'
                                    }
                                    onClick={() => handleHierarchySort(col.field)}
                                  >
                                    {col.label ?? formatHeaderLabel(col.field)}
                                  </TableSortLabel>
                                ) : (
                                  (col.label ?? formatHeaderLabel(col.field))
                                )}
                              </TableCell>
                            ))}
                          {listItemActions.length > 0 && (
                            <TableCell
                              align="right"
                              sx={{ fontWeight: 600, width: actionColumnWidth }}
                            >
                              Actions
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedHierarchyRows.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={
                                columnConfigs.filter(
                                  (c) => columnVisibilityModel[c.field] !== false,
                                ).length + (listItemActions.length > 0 ? 1 : 0)
                              }
                              align="center"
                              sx={{ py: 4 }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {`No ${entityConfig?.display_name?.toLowerCase() ?? 'items'} found. Create one to get started.`}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedHierarchyRows.map((row) => {
                            const depth = row.__depth ?? 0;
                            const hasChildren = Boolean(row.__hasChildren);
                            const isExpanded = Boolean(row.id && expandedNodeIds.has(row.id));

                            return (
                              <TableRow
                                key={row.id}
                                hover
                                onClick={() => {
                                  _onEntityClick?.(row);
                                }}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                  },
                                }}
                              >
                                {columnConfigs
                                  .filter((col) => columnVisibilityModel[col.field] !== false)
                                  .map((col) => {
                                    const raw = getValueAtPath(row, col.field);
                                    let displayValue: React.ReactNode;

                                    if (col.field === columnConfigs[0]?.field) {
                                      const size =
                                        col.field === 'display_name'
                                          ? getValueAtPath(row, 'entity_data.size')
                                          : undefined;
                                      const baseName =
                                        col.field === 'display_name'
                                          ? getValueAtPath(row, 'entity_data.name')
                                          : undefined;
                                      const label =
                                        size && baseName
                                          ? formatNomenclatureWithSize(
                                              String(baseName),
                                              String(size),
                                            )
                                          : String(raw ?? '');

                                      displayValue = (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            pl: depth * 3,
                                            gap: 0.5,
                                          }}
                                        >
                                          {hasChildren ? (
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                toggleNodeExpansion(row.id);
                                              }}
                                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                              sx={{ p: 0.5 }}
                                            >
                                              {isExpanded ? (
                                                <ArrowDownIcon fontSize="small" />
                                              ) : (
                                                <ArrowRightIcon fontSize="small" />
                                              )}
                                            </IconButton>
                                          ) : (
                                            <Box
                                              component="span"
                                              sx={{ width: 32, display: 'inline-flex' }}
                                            />
                                          )}
                                          <Typography variant="body2">{label}</Typography>
                                        </Box>
                                      );
                                    } else if (col.type === 'status') {
                                      const status = raw ? String(raw) : '';
                                      const color: 'default' | 'success' | 'warning' | 'error' =
                                        status === 'active'
                                          ? 'success'
                                          : status === 'inactive'
                                            ? 'warning'
                                            : 'error';
                                      displayValue = status ? (
                                        <Chip label={status} size="small" color={color} />
                                      ) : (
                                        '—'
                                      );
                                    } else if (col.type === 'boolean') {
                                      displayValue =
                                        raw === true || raw === 'true' ? (
                                          <CheckIcon fontSize="small" color="success" />
                                        ) : null;
                                    } else if (col.type === 'date' || col.type === 'dateTime') {
                                      displayValue = formatTemporalValue(raw, col.type);
                                    } else if (
                                      col.field === 'entity_identifier' &&
                                      entityType === 'ie_kit' &&
                                      raw
                                    ) {
                                      displayValue = formatIeKitId(String(raw));
                                    } else if (
                                      col.field === 'entity_identifier' &&
                                      entityType === 'person' &&
                                      raw
                                    ) {
                                      displayValue = String(raw).replace(
                                        /^INDV-/,
                                        personLabels.individualIdPrefix,
                                      );
                                    } else {
                                      displayValue =
                                        raw !== undefined && raw !== null ? String(raw) : '';
                                    }

                                    return (
                                      <TableCell key={col.field} align={col.align}>
                                        {displayValue}
                                      </TableCell>
                                    );
                                  })}
                                {listItemActions.length > 0 && (
                                  <TableCell align="right">
                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                      {listItemActions.map((action) => {
                                        const icon = actionIconMap[action.handler] ?? null;
                                        if (action.requiresOwnRecord && row.id !== myPersonEntityId)
                                          return null;
                                        return icon ? (
                                          <Tooltip key={action.id} title={action.label}>
                                            <span>
                                              <IconButton
                                                size="small"
                                                color="primary"
                                                aria-label={`${action.label}${row.display_name ? ` ${row.display_name}` : ''}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  void runEntityAction(action, row);
                                                }}
                                              >
                                                {icon}
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        ) : (
                                          <Button
                                            key={action.id}
                                            size="small"
                                            variant="outlined"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              void runEntityAction(action, row);
                                            }}
                                            sx={{ textTransform: 'none' }}
                                          >
                                            {action.label}
                                          </Button>
                                        );
                                      })}
                                    </Stack>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                ) : (
                  <DataGrid
                    apiRef={gridApiRef}
                    rows={rows}
                    columns={columns}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={handleVisibilityModelChange}
                    disableColumnFilter
                    localeText={{
                      noRowsLabel: `No ${entityConfig?.display_name?.toLowerCase() ?? 'items'} found. Create one to get started.`,
                    }}
                    getRowClassName={(params) =>
                      entityType === 'person' &&
                      myPersonEntityId &&
                      params.row.id === myPersonEntityId
                        ? 'EntityList--own-person-row'
                        : ''
                    }
                    sx={{
                      '& .MuiDataGrid-row:hover': {
                        cursor: 'pointer',
                      },
                      '& .EntityList--own-person-row': {
                        backgroundColor: '#EFF6FF',
                        '&:hover': { backgroundColor: '#DBEAFE' },
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
              }}
            >
              <EntityActivityPanel entityType={entityType} pollingInterval={5000} pageSize={25} />
            </Box>
          )}
        </CardContent>
      </Card>

      <Popover
        id={selectorId}
        open={selectorOpen}
        anchorEl={columnSelectorAnchor}
        onClose={() => setColumnSelectorAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, width: 240 } } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Visible Columns
        </Typography>
        <FormGroup>
          {columnConfigs.map((col) => (
            <FormControlLabel
              key={col.field}
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibilityModel[col.field] ?? true}
                  onChange={handleToggleColumn(col.field)}
                />
              }
              label={col.label ?? formatHeaderLabel(col.field)}
            />
          ))}
        </FormGroup>
      </Popover>
    </Box>
  );
};
